import * as React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import logo from '../images/icj.svg';

// 簡易パスワード認証（クライアント側判定）。
// 一般の閲覧者・検索エンジンへの目隠しであり、配信される JS / page-data からは本文を取得できる。
// パスワードを変更する場合は SHA-256 の16進ハッシュを差し替える。
const PASSWORD_HASH =
  'dda72186a6b082fc3840d29dbeef1a173919fcc617a12a23215beab9176426f5';
const STORAGE_KEY = 'icjllc-auth';

const theme = createTheme({
  palette: {
    primary: {
      main: '#121c3c'
    }
  }
});

const sha256 = async text => {
  const buffer = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(text)
  );
  return Array.from(new Uint8Array(buffer), b =>
    b.toString(16).padStart(2, '0')
  ).join('');
};

const readAuth = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === PASSWORD_HASH;
  } catch {
    return false;
  }
};

// wrapPageElement はページ遷移ごとに再マウントされるため、認証済みならすぐ描画できるよう保持する
let unlocked = false;

const writeAuth = () => {
  unlocked = true;
  try {
    sessionStorage.setItem(STORAGE_KEY, PASSWORD_HASH);
  } catch {
    // sessionStorage が使えない環境ではページ遷移ごとに再入力になる
  }
};

export const PasswordGate = ({ children }) => {
  // SSR とハイドレーション直後は何も描画せず、静的 HTML に本文を含めない
  const [status, setStatus] = React.useState(
    unlocked ? 'unlocked' : 'checking'
  );
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    if (readAuth()) {
      unlocked = true;
      setStatus('unlocked');
    } else {
      setStatus('locked');
    }
  }, []);

  const handleSubmit = async event => {
    event.preventDefault();
    if ((await sha256(password)) === PASSWORD_HASH) {
      writeAuth();
      setStatus('unlocked');
    } else {
      setError(true);
    }
  };

  if (status === 'unlocked') return children;
  if (status === 'checking') return null;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3,
          px: 2
        }}
      >
        <Box component='img' src={logo} alt='ICJ' sx={{ width: 120 }} />
        <Typography variant='body2' color='text.secondary'>
          このサイトは現在、関係者のみ閲覧できます。
        </Typography>
        <TextField
          type='password'
          label='パスワード'
          size='small'
          autoFocus
          autoComplete='current-password'
          value={password}
          onChange={event => {
            setPassword(event.target.value);
            setError(false);
          }}
          error={error}
          helperText={error ? 'パスワードが違います' : ' '}
          sx={{ width: '100%', maxWidth: 280 }}
        />
        <Button type='submit' variant='contained' disableElevation>
          表示する
        </Button>
      </Box>
    </ThemeProvider>
  );
};
